import { useEffect, useRef, useState } from "react";
import * as d3 from "d3";
import { useTranslation } from "react-i18next";

const BoxChart = ({ data, tripRte, tripDir, inbound }) => {
  const [t] = useTranslation("common");

  const chartRef = useRef();
  const [refreshKey, setRefreshKey] = useState(0);

  let in_label = inbound;

  useEffect(() => {
    // ✅ SAFE GUARDS
    if (!chartRef.current) return;

    const container = chartRef.current.parentElement;
    if (!container) return;

    const width = container.offsetWidth;
    const height = container.offsetHeight;

    if (!width || !height || !data?.length) return;

    const svg = d3.select(chartRef.current);

    // ✅ Clear only this chart
    svg.selectAll("*").remove();

    // ✅ Tooltip (scoped to this chart)
    let tooltip = d3.select(container).select(".tooltipotp");

    if (tooltip.empty()) {
      tooltip = d3
        .select(container)
        .append("div")
        .attr("class", "tooltipotp")
        .style("background", "white")
        .style("border", "1px solid #ccc");
    }

    const margins = { top: 20, right: 20, bottom: 70, left: 40 };

    // -------- DATA PREP --------

    const tripIds = Array.from(new Set(data.map((d) => d.trip_id)));

    const thresholds = tripIds
      .map((id) => {
        const row = data.find((d) => d.trip_id === id);
        return +row.plannedstarttime;
      })
      .sort((a, b) => a - b);

    const bins = d3
      .bin()
      .thresholds(thresholds)
      .value((d) => +d.plannedstarttime)(data)
      .map((bin) => {
        bin.sort((a, b) => a.realduration - b.realduration);

        const values = bin.map((d) => d.realduration);
        const min = values[0];
        const max = values[values.length - 1];

        const q1 = d3.quantile(values, 0.25);
        const q2 = d3.quantile(values, 0.5);
        const q3 = d3.quantile(values, 0.75);

        const iqr = q3 - q1;
        const r0 = Math.max(min, q1 - iqr * 1.5);
        const r1 = Math.min(max, q3 + iqr * 1.5);

        bin.quartiles = [q1, q2, q3];
        bin.range = [r0, r1];
        bin.outliers = bin.filter(
          (v) => v.realduration < r0 || v.realduration > r1,
        );

        bin.plannedDuration = d3.mean(bin.map((d) => d.plannedduration));
        bin.plannedStart = d3.mean(bin.map((d) => d.plannedstarttime));

        return bin;
      });

    // -------- SCALES --------

    const x = d3
      .scaleLinear()
      .domain([d3.min(bins, (d) => d.x0), d3.max(bins, (d) => d.x1)])
      .range([margins.left, width - margins.right]);

    const y = d3
      .scaleLinear()
      .domain([0, d3.max(bins, (d) => d.range[1])])
      .nice()
      .range([height - margins.bottom, margins.top]);

    const formatTime = (d) => {
      const date = new Date(1970, 0, 1, 0, 0, d);
      return d3.timeFormat("%H:%M:%S")(date);
    };

    const tickValues = bins.map((bin) => (bin.x0 + bin.x1) / 2);
    const tickLabels = bins.map((bin) => formatTime(bin.plannedStart));

    // -------- SVG --------

    svg.attr("width", width).attr("height", height);

    const g = svg.append("g").selectAll("g").data(bins).join("g");

    const maxWidth = 40;

    // -------- BOX --------

    g.append("path")
      .attr("fill", "#ddd")
      .attr("d", (d) => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;

        return `
          M${left},${y(d.quartiles[2])}
          H${right}
          V${y(d.quartiles[0])}
          H${left}
          Z
        `;
      })
      .on("mouseover", (event, d) => {
        const xPos = event.pageX;
        const yPos = event.pageY;

        tooltip
          .html(
            `
          Min : ${(d.range[0] / 60).toFixed(0)}<br/>
          Q1 : ${(d.quartiles[0] / 60).toFixed(0)}<br/>
          Median : ${(d.quartiles[1] / 60).toFixed(0)}<br/>
          Planned : ${(d.plannedDuration / 60).toFixed(0)}<br/>
          Q3 : ${(d.quartiles[2] / 60).toFixed(0)}<br/>
          Max : ${(d.range[1] / 60).toFixed(0)}
        `,
          )
          .style("position", "absolute")
          .style("left", xPos + 10 + "px")
          .style("top", yPos - 40 + "px")
          .style("opacity", 0.9);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    // -------- MEDIAN --------

    g.append("path")
      .attr("stroke", "black")
      .attr("stroke-width", 2)
      .attr("d", (d) => {
        const x0 = x(d.x0) + 1;
        const x1 = x(d.x1);

        const fullWidth = x1 - x0;
        const boxWidth = Math.min(fullWidth, maxWidth);

        const left = x0 + (fullWidth - boxWidth) / 2;
        const right = left + boxWidth;

        return `M${left},${y(d.quartiles[1])} H${right}`;
      })
      .on("mouseover", (event, d) => {
        const xPos = event.pageX;
        const yPos = event.pageY;

        tooltip
          .html(
            `
          Min : ${(d.range[0] / 60).toFixed(0)}<br/>
          Q1 : ${(d.quartiles[0] / 60).toFixed(0)}<br/>
          Median : ${(d.quartiles[1] / 60).toFixed(0)}<br/>
          Planned : ${(d.plannedDuration / 60).toFixed(0)}<br/>
          Q3 : ${(d.quartiles[2] / 60).toFixed(0)}<br/>
          Max : ${(d.range[1] / 60).toFixed(0)}
        `,
          )
          .style("position", "absolute")
          .style("left", xPos + 10 + "px")
          .style("top", yPos - 40 + "px")
          .style("opacity", 0.9);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    // -------- OUTLIERS --------

    g.append("g")
      .attr("transform", (d) => `translate(${x((d.x0 + d.x1) / 2)},0)`)
      .selectAll("circle")
      .data((d) => d.outliers)
      .join("circle")
      .attr("r", 2)
      .attr("cx", () => (Math.random() - 0.5) * 6)
      .attr("cy", (d) => y(d.realduration))
      .attr("fill", "black")
      .attr("opacity", 0.3);

    // -------- PLANNED DOT --------

    svg
      .selectAll(".planned-dot")
      .data(bins)
      .join("circle")
      .attr("class", "planned-dot")
      .attr("cx", (d) => x((d.x0 + d.x1) / 2))
      .attr("cy", (d) => y(d.plannedDuration))
      .attr("r", 3)
      .attr("fill", "red")
      .on("mouseover", (event, d) => {
        const xPos = event.pageX;
        const yPos = event.pageY;

        tooltip
          .html(
            `
          Min : ${(d.range[0] / 60).toFixed(0)}<br/>
          Q1 : ${(d.quartiles[0] / 60).toFixed(0)}<br/>
          Median : ${(d.quartiles[1] / 60).toFixed(0)}<br/>
          Planned : ${(d.plannedDuration / 60).toFixed(0)}<br/>
          Q3 : ${(d.quartiles[2] / 60).toFixed(0)}<br/>
          Max : ${(d.range[1] / 60).toFixed(0)}
        `,
          )
          .style("position", "absolute")
          .style("left", xPos + 10 + "px")
          .style("top", yPos - 40 + "px")
          .style("opacity", 0.9);
      })
      .on("mouseout", () => {
        tooltip.style("opacity", 0);
      });

    // -------- AXES --------

    svg
      .append("g")
      .attr("transform", `translate(0,${height - margins.bottom})`)
      .call(
        d3
          .axisBottom(x)
          .tickValues(tickValues)
          .tickFormat((d, i) => tickLabels[i]),
      )
      .selectAll("text")
      .attr("transform", "rotate(-90)")
      .attr("text-anchor", "end")
      .attr("dx", "-0.8em")
      .attr("dy", "-0.5em");

    svg
      .append("g")
      .attr("transform", `translate(${margins.left},0)`)
      .call(d3.axisLeft(y).tickFormat((d) => (d / 60).toFixed(0)))
      .call((g) => g.select(".domain").remove());

    // -------- RESIZE --------

    function updateGraph() {
      d3.select(chartRef.current).selectAll("*").remove();
      setRefreshKey((k) => k + 1);
    }

    d3.select(window).on("resize.boxchart", updateGraph);

    return () => {
      d3.select(window).on("resize.boxchart", null);
    };
  }, [data, refreshKey]);

  return <svg ref={chartRef}></svg>;
};

export default BoxChart;
